/* eslint-disable i18next/no-literal-string */

/**
 * @fileoverview
 *
 * @deprecated - transitioning to new analytics framework (see {@link utils/analytics/README.md})
 */

import { useEffect } from 'react';
import ReactGA from 'react-ga';
import Router from 'next/router';
import { useObserver } from 'mobx-react-lite';
import _once from 'lodash/once';
import _omitBy from 'lodash/omitBy';
import _isUndefined from 'lodash/isUndefined';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'hooks/use-ui';
import { isBrowser } from 'utils/misc-utils';
import PublicEnv from 'shared/utils/public-env';
import { createGaV4Client, createGaV4DelayedCallClient } from 'utils/analytics/clients/ga-v4-client';
import { isGaV4Id } from 'utils/analytics/helpers/is-ga-v4-id';

// Start with a delayed call client until we have access to the dispensary's GA ID
let dispensaryGaV4Client = createGaV4DelayedCallClient();

// a (read-only) accessor for the `let` above.
export const getDispensaryGaV4Client = () => dispensaryGaV4Client;

export const initGlobalTracker = _once(() => {
  if (PublicEnv.appEnv !== 'development') {
    try {
      ReactGA.initialize(PublicEnv.googleAnalyticsID, {
        gaOptions: {
          cookieFlags: 'max-age=7200;secure;samesite=none',
        },
      });
      ReactGA.plugin.require('ec');
    } catch (ex) {
      window?.Rollbar?.error('initGlobalTracker', ex);
    }
  }
});

const initDispensaryTracker = _once((id) => {
  if (isGaV4Id(id)) {
    const delayedCallClient = dispensaryGaV4Client;
    dispensaryGaV4Client = createGaV4Client(id);

    // replay all the events that were captured before the client
    // was initialized
    delayedCallClient?.updateClient(dispensaryGaV4Client);
  } else {
    ReactGA.ga('create', id, 'auto', 'dispensaryTracker', {
      allowLinker: true,
      cookieFlags: 'max-age=7200;secure;samesite=none',
    });
    ReactGA.ga('dispensaryTracker.require', 'ec');
  }
});

function embeddedEventDispatcher(type, event, data) {
  if (window.parent === window) {
    return true;
  }

  window.parent.postMessage(
    JSON.stringify({
      event: `analytics:${type}`,
      // Note: Playload was unfortunate typo that some stores may be using :(. Need to leave intact.
      payload: { event, playload: data, payload: data },
    }),
    '*'
  );
  return true;
}

export function dispatchOnMostTrackers(event, ...payload) {
  try {
    ReactGA.ga(event, ...payload);
    ReactGA.ga(`dispensaryTracker.${event}`, ...payload);
  } catch (ex) {
    window?.Rollbar?.error('ReactGA error', ex);
  }
  embeddedEventDispatcher('ga_event', event, payload);
}

export function setDimensionInfo(dispensary, variant) {
  if (!dispensary?.id) {
    window?.Rollbar?.warn(`setDimensionInfo with disp ${dispensary} and id ${dispensary?.id}`);
  }
  try {
    ReactGA.ga('set', 'dimension1', dispensary?.id);
    ReactGA.ga('set', 'dimension2', variant);
    ReactGA.ga('set', 'dimension3', dispensary?.name);
  } catch (ex) {
    window?.Rollbar?.error('setDimensionInfo', ex);
  }
  embeddedEventDispatcher('ga_dimensions', [dispensary.id, variant, dispensary.name]);
}

export function useDispensaryGA() {
  const UI = useUI();
  const { dispensary } = useDispensary();
  const shouldUseDispensaryGA = useObserver(() => UI.isEmbedded || UI.isStoreFront || UI.isPlus);
  const variant = useObserver(() => UI.variant);

  return useEffect(() => {
    if (!isBrowser()) {
      return;
    }
    if (dispensary) {
      initGlobalTracker();
      setDimensionInfo(dispensary, variant);
      if (dispensary.googleAnalyticsID && shouldUseDispensaryGA) {
        initDispensaryTracker(dispensary.googleAnalyticsID);
      }
    }
  }, [dispensary?.googleAnalyticsID, shouldUseDispensaryGA, variant]);
}

export function useTrackRouteChanges() {
  useEffect(() => {
    const handleRouteChange = (url) => ReactGA.pageview(url, ['dispensaryTracker']);
    Router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, []);
}

export function useInitGlobalGA() {
  return useEffect(() => {
    initGlobalTracker();
  });
}

export function cartItemToUAObject(item) {
  return _omitBy(
    {
      id: item.product.id,
      name: item.product.name,
      brand: item.product.brand?.name || item.product.brandName,
      category: item.product.type,
      variant: item.option,
      additional_variant: item.additionalOption,
      quantity: item.quantity,
      price: item.unitPrice,
      dimension1: item.product.DispensaryID,
    },
    _isUndefined
  );
}
